import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    compoundManagementSlice,
    getActiveCompoundManagementView,
    getIncomingVehicleBulkSelection,
    getOutgoingVehicleBulkSelection,
    getVehicleInventoryBulkSelection,
    LoadPreview,
} from '../../../reducers/compoundManagementSlice';
import {
    usePostLoadPreviewArrivalsMutation,
    usePostLoadPreviewDeparturesMutation,
    usePostLoadPreviewUnannouncedDeparturesMutation,
} from '../../../api/compoundManagementApi';
import { CompoundManagementView } from '../../compoundManagement.types';
import { runInBackground } from '../../../../../configuration/setup/backgroundActions';
import { MAX_BULK_SIZE } from '../bulkStatusUpdate/BulkStatusUpdateDialog';
import { handleQueryError } from '../../../notifications/ErrorNotification';

export const StartBulkStatusUpdateButton = () => {
    const dispatch = useAppDispatch();
    const currentCompoundView = useAppSelector(getActiveCompoundManagementView);

    const { getCurrentSelectedVehicleIds, getCorrectValidateFunction, isLoading } =
        useLoadPreviewSelection(currentCompoundView);

    const selectedIds = getCurrentSelectedVehicleIds();
    const selectedIdsCount = selectedIds?.length ?? 0;

    const handleClick = () => {
        if (selectedIdsCount === 0) {
            dispatch(compoundManagementSlice.actions.startBulkStatusUpdateWithEmptySelection());
            return;
        }

        if (selectedIdsCount > MAX_BULK_SIZE) {
            dispatch(compoundManagementSlice.actions.startBulkStatusUpdate(undefined));
            return;
        }

        runInBackground(async () => {
            try {
                const validateLoad = getCorrectValidateFunction();
                if (validateLoad !== undefined && selectedIds !== undefined) {
                    const result = await validateLoad({ ids: selectedIds, vins: [] }).unwrap();
                    const loadPreview = {
                        view: currentCompoundView,
                        ...result,
                    } as LoadPreview;
                    dispatch(compoundManagementSlice.actions.startBulkStatusUpdate(loadPreview));
                }
            } catch (error) {
                handleQueryError(error);
            }
        });
    };

    return (
        <Button
            bsStyle={Button.PRIMARY}
            className={isLoading ? 'btn-loading disabled' : ''}
            iconName={isLoading ? '' : 'rioglyph-cards-add'}
            onClick={handleClick}
        >
            <FormattedMessage
                id="outboundOrderBook.compoundManagement.toolbar.buildLoad"
                values={{ count: selectedIdsCount }}
            />
        </Button>
    );
};

export const useLoadPreviewSelection = (currentCompoundView: CompoundManagementView) => {
    const selectedIncomingVehicleIds = useAppSelector(getIncomingVehicleBulkSelection);
    const selectedInventoryVehicleIds = useAppSelector(getVehicleInventoryBulkSelection);
    const selectedOutgoingVehicleIds = useAppSelector(getOutgoingVehicleBulkSelection);

    const [validateIncomingLoad, incomingLoadValidationRequest] = usePostLoadPreviewArrivalsMutation();
    const [validateInventoryLoad, inventoryLoadValidationRequest] = usePostLoadPreviewUnannouncedDeparturesMutation();
    const [validateOutgoingLoad, outgoingLoadValidationRequest] = usePostLoadPreviewDeparturesMutation();

    const getCurrentSelectedVehicleIds = () => {
        switch (currentCompoundView) {
            case CompoundManagementView.INCOMING_VEHICLES:
                return selectedIncomingVehicleIds;
            case CompoundManagementView.INVENTORY:
                return selectedInventoryVehicleIds;
            case CompoundManagementView.OUTGOING_VEHICLES:
                return selectedOutgoingVehicleIds;
            default:
                return undefined;
        }
    };

    const getCorrectValidateFunction = () => {
        switch (currentCompoundView) {
            case CompoundManagementView.INCOMING_VEHICLES:
                return validateIncomingLoad;
            case CompoundManagementView.INVENTORY:
                return validateInventoryLoad;
            case CompoundManagementView.OUTGOING_VEHICLES:
                return validateOutgoingLoad;
            default:
                return undefined;
        }
    };

    const isLoading =
        inventoryLoadValidationRequest.isLoading ||
        outgoingLoadValidationRequest.isLoading ||
        incomingLoadValidationRequest.isLoading;

    return { getCurrentSelectedVehicleIds, getCorrectValidateFunction, isLoading };
};
